import { useEffect, useMemo, useState } from 'react';

import { Alert, Button, Calendario, Checkbox, Input, Label, Select } from '@/components/ui';
import { cn } from '@/lib/cn';
import { isValidCNPJ, isValidCPF, maskCNPJ, maskCPF } from '@/lib/format-doc';

import {
  submitForm,
  useFormSchema,
  type FormFieldSchema,
  type FormSchema,
  type ScopeTipo,
  type SubmitFormError,
} from './useFormSchema';

/**
 * DynamicForm — renderiza form dinâmico baseado em FormSchema do backend (A6.6).
 *
 * Uso:
 *   <DynamicForm
 *     entidade="deal"
 *     entidadeId="deal-123"
 *     scopeTipo="workflow"
 *     scopeId="wf-orc"
 *     onSubmitted={() => console.log('persistido')}
 *   />
 *
 * Quando entidadeId é fornecido, busca valores atuais + permite edit.
 * Quando entidadeId NULL, é form de criação — usuário precisa salvar a
 * entidade ANTES de preencher custom fields (o parent decide).
 *
 * ⚠️ ESTE É O ÚNICO RENDERIZADOR de campos personalizados do sistema (K-01,
 * 2026-07-30). Até então existia um segundo, inline na
 * `src/pages/publico/FormCaptacaoPage.tsx` (bloco "PF9"), que era o que
 * rodava em produção — mudança feita só aqui não chegava em quem preenchia o
 * formulário público. O inline foi removido e a página passou a alimentar
 * este componente por `schemaExterno`. **Não recrie um renderizador local em
 * lugar nenhum**: qualquer tela nova (ficha do lead, ficha do cliente,
 * OS/Manutenção) monta o `DynamicForm`. Ver
 * `docs/architecture/CAMPOS-PERSONALIZADOS-DECISAO.md`.
 *
 * NÃO REVERTER:
 *   - Apenas campos VISÍVEIS + EDITÁVEIS no contexto são renderizados
 *   - Campos `computed` mostram apenas resultado (read-only)
 *   - Submit valida client-side mínimo (obrigatórios não vazios) e
 *     deixa server-side validar formato (CPF/CNPJ/etc) com mensagens
 *     específicas. Os avisos de formato de CPF/CNPJ/JSON abaixo do campo
 *     são SÓ aviso — não bloqueiam o submit, o servidor é a fonte da verdade
 *   - Em 422, mapeia violacoes pelo customFieldId pra mostrar erro
 *     inline em cada campo
 *
 * Tipos com renderização real (19 dos 21): text, textarea, number, currency,
 * percentage, email, phone, cpf, cnpj, url, date, datetime, time, dropdown,
 * multi_select, radio, checkbox, computed e json.
 *
 * Os 2 restantes — file e image — dependem de um endpoint de upload por campo
 * personalizado que não existe. A FASE-C tirou os dois da oferta do construtor
 * (`TIPOS_EM_PREPARACAO` em `admin/custom-fields/store.ts`), então campo novo
 * não nasce mais nesses tipos. `<UnsupportedField>` segue aqui só pros campos
 * criados antes da regra — quando o upload existir, o renderer entra no switch
 * e os dois saem do `TIPOS_EM_PREPARACAO`.
 */
export interface DynamicFormProps {
  entidade: string;
  entidadeId?: string | null;
  scopeTipo?: ScopeTipo;
  scopeId?: string;
  /** Callback após submit bem-sucedido. */
  onSubmitted?: () => void;
  /** Override do label do botão submit. Default: "Salvar campos personalizados". */
  submitLabel?: string;
  /** Esconde botão submit (caller assume controle do submit). */
  noSubmit?: boolean;
  /** Quando noSubmit=true, expõe valores via callback. */
  onValuesChange?: (values: Record<string, unknown>) => void;
  /** Dados adicionais pra avaliação de condições (regras). */
  contextDados?: Record<string, unknown>;
  /**
   * Schema pronto, vindo de fora. Quando preenchido o componente NÃO busca
   * nada — nem chama `GET /api/v1/forms/schema`.
   *
   * Existe pro formulário PÚBLICO de captação (`/f/:slug`), que não tem
   * sessão: o schema dele vem de `GET /api/v1/public/captacao/form-config`,
   * endpoint sem auth que devolve SÓ os campos marcados como públicos. Abrir
   * o `/forms/schema` seria pior — ele aceita `entidade` livre por
   * querystring e resolve visibilidade por perfil, então sem auth qualquer
   * visitante pediria o schema de qualquer entidade.
   *
   * ⚠️ Passe um objeto ESTÁVEL (`useMemo`): ele é dependência do efeito que
   * inicializa os valores. Objeto novo a cada render = valores resetados a
   * cada render (a pessoa não consegue digitar).
   */
  schemaExterno?: FormSchema;
  /**
   * Prefixo do `id` e do `data-testid` de cada campo. Default `'cf'` →
   * `cf-<customFieldId>`.
   *
   * O formulário público usa `'cap-cf'` porque `cap-cf-<customFieldId>` já é
   * contrato dos testes de lá desde o PF9 — o prefixo existe pra migração pro
   * renderizador canônico não quebrar nenhum seletor.
   */
  testIdPrefix?: string;
}

export function DynamicForm({
  entidade,
  entidadeId,
  scopeTipo,
  scopeId,
  onSubmitted,
  submitLabel = 'Salvar campos personalizados',
  noSubmit,
  onValuesChange,
  schemaExterno,
  testIdPrefix = 'cf',
}: DynamicFormProps) {
  const { data: schemaBuscado, isLoading } = useFormSchema({
    entidade,
    entidadeId,
    scopeTipo,
    scopeId,
    enabled: !schemaExterno,
  });
  const schema = schemaExterno ?? schemaBuscado;

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Inicializa values com valor atual de cada campo
  useEffect(() => {
    if (!schema) return;
    const init: Record<string, unknown> = {};
    for (const f of schema.campos) {
      init[f.id] = f.valor ?? f.defaultValue ?? '';
    }
    setValues(init);
  }, [schema]);

  // Notifica caller (quando noSubmit=true)
  useEffect(() => {
    if (noSubmit && onValuesChange) onValuesChange(values);
  }, [values, noSubmit, onValuesChange]);

  // Agrupa campos por `grupo` (campos sem grupo ficam em "" no final)
  const grouped = useMemo(() => {
    if (!schema) return new Map<string, FormFieldSchema[]>();
    const map = new Map<string, FormFieldSchema[]>();
    for (const f of schema.campos) {
      const key = f.grupo ?? '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(f);
    }
    return map;
  }, [schema]);

  function handleChange(field: FormFieldSchema, newValue: unknown) {
    setValues((v) => ({ ...v, [field.id]: newValue }));
    if (errors[field.id]) {
      setErrors((e) => {
        const ne = { ...e };
        delete ne[field.id];
        return ne;
      });
    }
  }

  function clientValidate(): boolean {
    if (!schema) return false;
    const newErrors: Record<string, string> = {};
    for (const f of schema.campos) {
      if (f.obrigatorio) {
        const v = values[f.id];
        const isEmpty =
          v === null || v === undefined || v === '' ||
          (Array.isArray(v) && v.length === 0);
        if (isEmpty) {
          newErrors[f.id] = f.obrigatoriedadeMensagem ?? `${f.label} é obrigatório`;
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit() {
    if (!schema || !entidadeId) {
      setSubmitError('entidadeId é obrigatório pra salvar campos personalizados.');
      return;
    }
    if (!clientValidate()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await submitForm({
        entidade: schema.entidade,
        entidadeId,
        scopeTipo: schema.scopeTipo ?? undefined,
        scopeId: schema.scopeId ?? undefined,
        valores: values,
      });
      if (res.error) {
        if (res.violacoes && Array.isArray(res.violacoes)) {
          const newErrors: Record<string, string> = {};
          for (const v of res.violacoes as SubmitFormError[]) {
            newErrors[v.customFieldId] = v.mensagem;
          }
          setErrors(newErrors);
          setSubmitError(`Validação falhou: ${res.violacoes.length} erro(s)`);
        } else {
          setSubmitError(res.error);
        }
      } else {
        onSubmitted?.();
      }
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Erro desconhecido');
    } finally {
      setSubmitting(false);
    }
  }

  // Com schema injetado a query nem roda — o "Carregando" seria eterno.
  if (isLoading && !schemaExterno) {
    return (
      <p className="text-sm text-ink-3 dark:text-ink-dark-3">
        Carregando campos personalizados…
      </p>
    );
  }
  // `?.` também em `campos`: resposta sem essa chave (API antiga, erro com
  // corpo diferente, mock que não conhece o endpoint) estourava aqui e derrubava
  // a tela hospedeira junto. Um formulário opcional não pode ter esse poder.
  if (!schema?.campos?.length) {
    return null; // sem campos custom configurados pra esse contexto
  }

  return (
    <div className="space-y-6">
      {submitError && <Alert variant="danger">{submitError}</Alert>}

      {[...grouped.entries()].map(([grupo, fields]) => (
        <fieldset key={grupo || 'sem-grupo'} className="space-y-4">
          {grupo && (
            <legend className="text-sm font-semibold text-ink-1 dark:text-ink-dark-1 mb-2">
              {grupo}
            </legend>
          )}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {fields.map((f) => (
              <div key={f.id} className={cn(larguraToCols(f.largura))}>
                <DynamicField
                  field={f}
                  value={values[f.id]}
                  onChange={(v) => handleChange(f, v)}
                  error={errors[f.id]}
                  testIdPrefix={testIdPrefix}
                />
              </div>
            ))}
          </div>
        </fieldset>
      ))}

      {!noSubmit && (
        <div className="flex justify-end pt-4 border-t border-line dark:border-line-dark">
          <Button onClick={handleSubmit} disabled={submitting || !entidadeId}>
            {submitting ? 'Salvando…' : submitLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

function larguraToCols(largura: FormFieldSchema['largura']): string {
  switch (largura) {
    case 'half': return 'md:col-span-6';
    case 'third': return 'md:col-span-4';
    case 'quarter': return 'md:col-span-3';
    case 'full':
    default: return 'md:col-span-12';
  }
}

/**
 * DynamicField — renderiza 1 campo conforme o tipo. Só file/image caem em
 * `<UnsupportedField>` (ver o cabeçalho do arquivo).
 */
interface DynamicFieldProps {
  field: FormFieldSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
  /** Ver `DynamicFormProps.testIdPrefix`. */
  testIdPrefix: string;
}

function DynamicField({ field, value, onChange, error, testIdPrefix }: DynamicFieldProps) {
  const disabled = !field.editable;
  const stringValue = value == null ? '' : String(value);
  /**
   * `id` do controle E `data-testid` — os dois saem daqui pra nunca
   * divergirem. `cf-<id>` nas telas internas, `cap-cf-<id>` no formulário
   * público (contrato dos testes de lá desde o PF9).
   */
  const fid = `${testIdPrefix}-${field.id}`;

  const labelEl = (
    <Label htmlFor={fid} required={field.obrigatorio}>
      {field.label}
    </Label>
  );

  const descricaoEl = field.descricao && (
    <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">{field.descricao}</p>
  );

  const errorEl = error && (
    <p className="text-xs text-red mt-1">{error}</p>
  );

  /**
   * Aviso de formato enquanto a pessoa digita (CPF/CNPJ/JSON). Some quando o
   * servidor devolve erro pro mesmo campo — o 422 é mais específico e não
   * pode competir com o aviso local.
   */
  function avisoFormato(texto: string) {
    if (error) return null;
    return (
      <p
        className="text-xs text-ink-2 dark:text-ink-dark-2 mt-1"
        data-testid={`${fid}-aviso`}
      >
        {texto}
      </p>
    );
  }

  switch (field.tipo) {
    case 'text':
    case 'email':
    case 'phone':
    case 'url':
      return (
        <div>
          {labelEl}
          <Input
            id={fid}
            data-testid={fid}
            type={field.tipo === 'email' ? 'email' : field.tipo === 'url' ? 'url' : 'text'}
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder ?? undefined}
            disabled={disabled}
            maxLength={maxLengthDe(field)}
          />
          {descricaoEl}
          {errorEl}
        </div>
      );

    case 'textarea':
      return (
        <div>
          {labelEl}
          <textarea
            id={fid}
            data-testid={fid}
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder ?? undefined}
            disabled={disabled}
            rows={3}
            maxLength={maxLengthDe(field)}
            className="w-full rounded-lg border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface px-3 py-2 text-sm text-ink-1 dark:text-ink-dark-1 focus:outline-none focus:border-red disabled:opacity-50"
          />
          {descricaoEl}
          {errorEl}
        </div>
      );

    case 'number':
    case 'currency':
    case 'percentage':
      return (
        <div>
          {labelEl}
          <div className="flex items-center gap-1">
            {field.prefix && (
              <span className="text-sm text-ink-3 dark:text-ink-dark-3">
                {field.prefix}
              </span>
            )}
            <Input
              id={fid}
              data-testid={fid}
              type="number"
              value={stringValue}
              onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
              placeholder={field.placeholder ?? undefined}
              disabled={disabled}
              step={field.tipo === 'percentage' ? 0.01 : undefined}
            />
            {field.suffix && (
              <span className="text-sm text-ink-3 dark:text-ink-dark-3">
                {field.suffix}
              </span>
            )}
          </div>
          {descricaoEl}
          {errorEl}
        </div>
      );

    // CPF/CNPJ: máscara progressiva enquanto digita. O dígito verificador
    // é conferido de novo no servidor (validator.ts) — aqui é só aviso.
    case 'cpf':
    case 'cnpj': {
      const isCpf = field.tipo === 'cpf';
      const mascarado = isCpf ? maskCPF(stringValue) : maskCNPJ(stringValue);
      const completo = mascarado.replace(/\D/g, '').length === (isCpf ? 11 : 14);
      const valido = isCpf ? isValidCPF(mascarado) : isValidCNPJ(mascarado);
      return (
        <div>
          {labelEl}
          <Input
            id={fid}
            data-testid={fid}
            type="text"
            inputMode="numeric"
            value={mascarado}
            onChange={(e) =>
              onChange(isCpf ? maskCPF(e.target.value) : maskCNPJ(e.target.value))
            }
            placeholder={field.placeholder ?? (isCpf ? '000.000.000-00' : '00.000.000/0000-00')}
            maxLength={isCpf ? 14 : 18}
            disabled={disabled}
          />
          {descricaoEl}
          {errorEl}
          {completo && !valido &&
            avisoFormato(
              isCpf
                ? 'Esse CPF não confere. Verifique os números digitados.'
                : 'Esse CNPJ não confere. Verifique os números digitados.',
            )}
        </div>
      );
    }

    // 13º ponto do calendário (RITUAL-ITEMS-1-4): o último `datetime-local`
    // do sistema morava aqui. Data e data+hora usam o <Calendario> canônico
    // (na página pública as queries de expediente ficam desligadas sem token
    // e o componente degrada pro fallback de fim de semana). Só-hora não tem
    // variante no Calendario — segue input nativo.
    case 'date':
    case 'datetime':
      return (
        <div>
          {labelEl}
          <Calendario
            valor={stringValue}
            onChange={onChange}
            soData={field.tipo === 'date'}
            desabilitado={disabled}
            testId={fid}
          />
          {descricaoEl}
          {errorEl}
        </div>
      );

    case 'time':
      return (
        <div>
          {labelEl}
          <Input
            id={fid}
            data-testid={fid}
            type="time"
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {descricaoEl}
          {errorEl}
        </div>
      );

    case 'dropdown':
      return (
        <div>
          {labelEl}
          <Select
            id={fid}
            data-testid={fid}
            value={stringValue}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          >
            <option value="">— Selecione —</option>
            {field.opcoes.map((op) => (
              <option key={String(op.value)} value={String(op.value)}>
                {op.label ?? String(op.value)}
              </option>
            ))}
          </Select>
          {descricaoEl}
          {errorEl}
        </div>
      );

    // Marcar várias: o valor trafega como array de strings — é assim que o
    // servidor valida (validator.ts `multi_select`) e persiste (valor_json).
    case 'multi_select': {
      const marcados = toStringArray(value);
      return (
        <div>
          {labelEl}
          <div
            role="group"
            aria-label={field.label}
            data-testid={`${fid}-opcoes`}
            className="flex flex-col gap-2 rounded-xl border border-line dark:border-line-dark px-3 py-2"
          >
            {field.opcoes.length === 0 && (
              <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                Nenhuma opção cadastrada para este campo.
              </p>
            )}
            {field.opcoes.map((op) => {
              const v = String(op.value);
              return (
                <Checkbox
                  key={v}
                  label={op.label ?? v}
                  checked={marcados.includes(v)}
                  disabled={disabled}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...marcados, v]
                        : marcados.filter((m) => m !== v),
                    )
                  }
                />
              );
            })}
          </div>
          {descricaoEl}
          {errorEl}
        </div>
      );
    }

    // Opções à vista: mesmo valor do dropdown (uma string), só muda a forma
    // de escolher. `name` compartilhado agrupa os rádios.
    case 'radio':
      return (
        <div>
          {labelEl}
          <div
            role="radiogroup"
            aria-label={field.label}
            data-testid={`${fid}-opcoes`}
            className="flex flex-col gap-2 rounded-xl border border-line dark:border-line-dark px-3 py-2"
          >
            {field.opcoes.length === 0 && (
              <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                Nenhuma opção cadastrada para este campo.
              </p>
            )}
            {field.opcoes.map((op) => {
              const v = String(op.value);
              return (
                <label
                  key={v}
                  className={cn(
                    'flex items-center gap-2 text-sm text-ink-1 dark:text-ink-dark-1',
                    disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                  )}
                >
                  <input
                    type="radio"
                    name={fid}
                    value={v}
                    checked={stringValue === v}
                    onChange={() => onChange(v)}
                    disabled={disabled}
                    className="accent-red"
                  />
                  <span>{op.label ?? v}</span>
                </label>
              );
            })}
          </div>
          {descricaoEl}
          {errorEl}
        </div>
      );

    case 'checkbox':
      return (
        <div>
          <label htmlFor={fid} className="flex items-center gap-2 cursor-pointer">
            <input
              id={fid}
              data-testid={fid}
              type="checkbox"
              checked={value === true || value === 1 || value === '1' || value === 'true'}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
            />
            <span className="text-sm text-ink-1 dark:text-ink-dark-1">
              {field.label}
              {/* O checkbox não usa o <Label> (o rótulo fica À DIREITA da
                  caixinha), então o asterisco de obrigatório vem na mão —
                  sem ele um consentimento obrigatório não se anuncia. */}
              {field.obrigatorio && <span className="text-red ml-1" aria-hidden="true">*</span>}
            </span>
          </label>
          {descricaoEl}
          {errorEl}
        </div>
      );

    case 'computed':
      return (
        <div>
          {labelEl}
          <div className="px-3 py-2 rounded-lg border border-line dark:border-line-dark bg-paper-warm dark:bg-paper-dark-warm text-sm text-ink-2 dark:text-ink-dark-2 italic">
            Calculado: {stringValue || '(aguardando dados)'}
          </div>
          {descricaoEl}
        </div>
      );

    // Dados livres: o schema devolve o JSON já parseado (schema-generator
    // `extractValor`), então voltamos pra texto pra editar. O que sobe é a
    // string crua — o servidor parseia e guarda em valor_json.
    case 'json': {
      const texto = jsonParaTexto(value);
      const jsonInvalido = texto.trim() !== '' && !isJsonValido(texto);
      return (
        <div>
          {labelEl}
          <textarea
            id={fid}
            data-testid={fid}
            value={texto}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder ?? '{"chave": "valor"}'}
            disabled={disabled}
            rows={4}
            spellCheck={false}
            className="w-full rounded-lg border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface px-3 py-2 font-mono text-xs text-ink-1 dark:text-ink-dark-1 focus:outline-none focus:border-red disabled:opacity-50"
          />
          {descricaoEl}
          {errorEl}
          {jsonInvalido &&
            avisoFormato('O texto ainda não está num formato válido — confira chaves, aspas e vírgulas.')}
        </div>
      );
    }

    default:
      return <UnsupportedField field={field} fid={fid} />;
  }
}

/**
 * Teto de caracteres declarado na validação do campo (`validacao.maxLength` —
 * mesma chave que o validator do servidor lê).
 *
 * Sem isso a pessoa digita à vontade e o servidor corta o excedente CALADO no
 * salvamento — perde texto sem avisar ninguém. O `maxLength` no input trava
 * na hora, e o servidor continua sendo a fonte da verdade.
 */
function maxLengthDe(field: FormFieldSchema): number | undefined {
  const bruto = field.validacao?.maxLength;
  return typeof bruto === 'number' && bruto > 0 ? bruto : undefined;
}

/** Normaliza o valor de um multi_select pra array de strings. */
function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value === 'string' && value.trim() !== '') {
    // Valor pode chegar como JSON cru se o backend não parseou.
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map((v) => String(v));
    } catch { /* não era JSON — trata como valor único */ }
    return [value];
  }
  return [];
}

/** Valor de um campo json → texto editável no textarea. */
function jsonParaTexto(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function isJsonValido(texto: string): boolean {
  try {
    JSON.parse(texto);
    return true;
  } catch {
    return false;
  }
}

/**
 * Só alcança file/image — os dois tipos que saíram da oferta do construtor
 * (`TIPOS_EM_PREPARACAO`) e sobrevivem apenas em campos criados antes da regra.
 *
 * FASE-C (copy): o aviso dizia "ainda não tem renderer" e citava o código
 * interno da pendência ("TD-A6.6-fields"). Quem vê isso é quem está
 * preenchendo o formulário, não quem programa — a frase agora diz o que
 * aconteceu e o que fazer.
 */
function UnsupportedField({ field, fid }: { field: FormFieldSchema; fid: string }) {
  return (
    <div
      data-testid={`${fid}-sem-renderer`}
      className="min-w-0 rounded-xl border border-line px-3 py-2 dark:border-line-dark"
    >
      <p className="text-xs text-ink-1 dark:text-ink-dark-1">
        O campo <strong>{field.label}</strong> não pode ser preenchido por aqui.
      </p>
      {/* O menu real é Configurações › Sistema › Campos Personalizados —
          "Cadastros" não existe e nunca existiu. Mandar a pessoa procurar um
          menu inventado é pior do que não dizer onde fica. */}
      <p className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2">
        Peça a quem administra o sistema pra trocar o tipo deste campo em
        Configurações › Catálogos › Campos personalizados.
      </p>
    </div>
  );
}
